import { useState } from 'react';
import { FileCode, History } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { ReportBundleVersionParams, ReportBundleVersionSummary } from '@/backend/access/reportBundle/types';
import { useReportBundleStore } from '@/frontend/store/ReportBundleStore';
import { shortName } from '@/frontend/lib/people';
import { ReportBundleFiles } from '@/reports/bundles/types';
import { sheetChanges } from '@/reports/bundles/inputSheet';
import { InputSheet, sheetInputs } from '@/reports/inputs';
import { IdChangeList } from './inputs/IdChanges';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

interface BundleVersionsPanelProps {
  bundleId: string;
  versions: ReportBundleVersionSummary[];
  onRestore: (files: ReportBundleFiles) => void;
}

/** The bundle's published versions: read a version's source, or bring it back into the draft. */
export function BundleVersionsPanel({ bundleId, versions, onRestore }: BundleVersionsPanelProps) {
  const fetchVersion = useReportBundleStore((s) => s.fetchVersion);
  const [viewing, setViewing] = useState<ReportBundleVersionParams | null>(null);
  const [restoring, setRestoring] = useState<ReportBundleVersionSummary | null>(null);

  if (versions.length === 0) {
    return <p className="max-w-prose p-[1rem] text-xs text-muted-foreground">{I18n.get('reportBundleNoVersions')}</p>;
  }

  return (
    <div>
      <ol aria-label={I18n.get('designerVersions')} className="divide-y">
        {versions.map((v, i) => (
          <li key={v.id} className="flex flex-col gap-1 px-3 py-2.5 text-xs">
            <span className="flex items-baseline gap-2">
              <span className="text-sm font-medium">v{v.number}</span>
              <span className="text-muted-foreground">{v.definitionVersion}</span>
            </span>
            <span className="text-muted-foreground" title={v.publishedBy}>{new Date(v.createdAt ?? '').toLocaleString()} · {shortName(v.publishedBy)}</span>
            {v.note && <span className="[overflow-wrap:anywhere]">{v.note}</span>}
            {v.meta.schema?.version && <span className="text-muted-foreground">{I18n.get('rv2SchemaVersionShort', { number: String(v.meta.schema.version) })}</span>}
            <SheetChanges sheet={v.meta.sheet} previous={versions[i + 1]?.meta.sheet} />
            <span className="mt-0.5 flex flex-wrap gap-1">
              <Button variant="outline" size="xs" onClick={() => void fetchVersion(bundleId, v.id).then((full) => full && setViewing(full))}><FileCode /> {I18n.get('reportBundleSource')}</Button>
              <Button variant="ghost" size="xs" onClick={() => setRestoring(v)}><History /> {I18n.get('reportBundleRestoreVersion')}</Button>
            </span>
          </li>
        ))}
      </ol>
      <Dialog open={viewing !== null} onOpenChange={(open) => { if (!open) setViewing(null); }}>
        {viewing && (
          <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-4xl">
            <DialogHeader><DialogTitle>v{viewing.number} · {viewing.title}</DialogTitle></DialogHeader>
            <div className="-mx-4 min-h-0 flex-1 overflow-auto px-4">
              {Object.entries(viewing.files).map(([path, content]) => (
                <details key={path} open={path.endsWith('definition.ts')} className="mb-2 rounded-lg border-[1px]">
                  <summary className="cursor-pointer px-2 py-1.5 font-mono text-xs">{path}</summary>
                  <pre className="overflow-auto border-t bg-muted/40 p-2 font-mono text-xs whitespace-pre-wrap">{content}</pre>
                </details>
              ))}
            </div>
          </DialogContent>
        )}
      </Dialog>
      <AlertDialog open={restoring !== null} onOpenChange={(open) => { if (!open) setRestoring(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{I18n.get('reportBundleRestoreVersion')}</AlertDialogTitle>
            <AlertDialogDescription>{I18n.get('reportBundleRestoreConfirm', { number: String(restoring?.number ?? '') })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{I18n.get('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const version = restoring;
                setRestoring(null);
                if (version) void fetchVersion(bundleId, version.id).then((full) => { if (full) onRestore(full.files); });
              }}
            >
              {I18n.get('reportBundleRestoreVersion')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * What a version did to the input sheet against the version before it: the
 * ids it changed, which anything reading inputs by id stops finding, and the
 * ones it added. Labels, units and defaults are not ids and go unmentioned.
 */
export function SheetChanges({ sheet, previous }: { sheet: InputSheet | undefined; previous: InputSheet | undefined }) {
  if (!sheet) return <span className="text-muted-foreground">—</span>;
  if (!previous) return <span>{I18n.get('rv2SheetInputs', { count: String(sheetInputs(sheet).length) })}</span>;
  const { changed, added } = sheetChanges(previous, sheet);
  if (changed.length === 0 && added.length === 0) return <span className="text-muted-foreground">{I18n.get('rv2SheetNoIdChanges')}</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {changed.length > 0 && (
        <details>
          <summary className="cursor-pointer font-medium text-amber-700">{I18n.get('rv2SheetIdsChanged', { count: String(changed.length) })}</summary>
          <IdChangeList changes={changed} shown={20} className="mt-1" />
        </details>
      )}
      {added.length > 0 && <span className="text-muted-foreground" title={added.join(', ')}>{I18n.get('rv2SheetAdded', { count: String(added.length) })}</span>}
    </div>
  );
}
